import { API_BASE, api } from '../services/api';

/**
 * Browser-side authentication session: token storage, sign-in transport and
 * identity lookup. The React-facing state machine lives in
 * `src/contexts/AuthContext.tsx`; this module contains no React.
 */

/**
 * localStorage key under which the bearer token is kept.
 *
 * NOTE: `src/services/api.ts` and `src/lib/api/geologist.ts` were inherited
 * from the host application and repeat this literal internally. They are the
 * readers; this constant is the writer. Folding those five occurrences into
 * this constant is a one-line change per site and is left as a follow-up so
 * that the extraction commit does not touch inherited files.
 */
export const AUTH_TOKEN_STORAGE_KEY = 'bentonit_token';

/**
 * Identity returned by `GET /api/v1/auth/me`.
 * Mirrors `UserResponse` in `backend/app/routers/auth.py`.
 */
export interface CurrentUser {
  id: string;
  email: string;
  full_name: string | null;
  /**
   * Server-authoritative role name. Deliberately a plain string: the backend
   * owns the role vocabulary, and an unknown value must degrade to "no
   * permissions" rather than be silently rewritten client-side. Every
   * authorisation decision goes through `canUserPerform()` in
   * `src/lib/permissions.ts`, which already returns false for unknown roles.
   */
  role: string;
}

interface AccessTokenResponse {
  access_token: string;
  token_type: string;
}

const LOGIN_ENDPOINT = `${API_BASE}/api/v1/auth/login`;

export function readAccessToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
}

export function storeAccessToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

/**
 * Exchanges credentials for a bearer token against `POST /api/v1/auth/login`
 * (JSON body `{ email, password }`, see `backend/app/routers/auth.py`).
 *
 * Deliberately issued with `fetch` rather than through `ApiClient`: the shared
 * client treats every 401 as "session expired" and hard-navigates to /login,
 * which for the sign-in call itself would reload the page and discard the
 * "wrong password" message before the user could read it.
 *
 * The 401 message stays deliberately undifferentiated — the backend returns one
 * indistinguishable error for unknown address, wrong password and disabled
 * account so that the form cannot be used to enumerate users. The client must
 * not reintroduce that oracle by guessing a more specific wording.
 */
export async function requestAccessToken(email: string, password: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(LOGIN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new Error('Не удалось связаться с сервером. Проверьте подключение и попробуйте снова.');
  }

  if (response.status === 401) {
    throw new Error('Неверный адрес электронной почты или пароль.');
  }
  if (response.status === 429) {
    throw new Error('Слишком много попыток входа. Повторите попытку через минуту.');
  }
  if (response.status === 422) {
    throw new Error('Проверьте адрес электронной почты и пароль.');
  }
  if (!response.ok) {
    throw new Error('Ошибка сервера, попробуйте позже.');
  }

  const payload = (await response.json()) as Partial<AccessTokenResponse>;
  if (!payload.access_token) {
    throw new Error('Сервер не вернул токен доступа.');
  }
  return payload.access_token;
}

/**
 * Loads the signed-in account. Uses the shared client so that the bearer token
 * and the global session-expiry handling stay in one place.
 */
export function fetchCurrentUser(): Promise<CurrentUser> {
  return api.request<CurrentUser>('/auth/me', { silent: true });
}

/** True when a rejection represents an expired or invalid session. */
export function isUnauthorized(cause: unknown): boolean {
  if (!(cause instanceof Error)) return false;
  if (cause.message.includes('Unauthorized')) return true;
  return (cause as Error & { status?: number }).status === 401;
}
